import { useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import { StartAndMonitorFlow } from '../../contentService/FlowStartandMonitor'
import { PollingFlowStatus } from '../../contentService/PollingFlowStatus'
import {
  canStartProcessAtom,
  processResultAtom,
  selectedLessonIdsAtom,
} from './atoms'
import {
  useProcessStartFlow,
  useExistingProcessFlowId,
  useProcessFlowComplete,
  useExistingFlowPoll,
  useResetProcessStage,
} from './hooks'
import { ResetStage } from './ResetStage'
import type { StageProps } from './types'

export function ProcessStage({ treeId, versionId }: StageProps) {
  const canStart = useAtomValue(canStartProcessAtom)
  const processResult = useAtomValue(processResultAtom)
  const setSelectedLessonIds = useSetAtom(selectedLessonIdsAtom)
  const startFlowFunction = useProcessStartFlow(treeId, versionId)
  const { existingFlowId, isChecking, clearExistingFlowId } =
    useExistingProcessFlowId(treeId, versionId)
  const { handleFlowComplete, error, clearError } = useProcessFlowComplete(
    treeId,
    versionId,
  )
  const onExistingFlowPoll = useExistingFlowPoll(
    handleFlowComplete,
    clearExistingFlowId,
  )
  const { resetProcessStage, isResetting } = useResetProcessStage(
    treeId,
    versionId,
  )
  const [processFlowResetTrigger, setProcessFlowResetTrigger] = useState(1)

  return (
    <Box border={1} borderColor="grey.300" borderRadius={1} p={2} mb={2}>
      <Typography variant="h6" gutterBottom sx={{ fontStyle: 'italic' }}>
        Generating Slides
      </Typography>

      {!isChecking && existingFlowId && (
        <PollingFlowStatus
          flowId={existingFlowId}
          onFlowPoll={onExistingFlowPoll}
          pollingIntervalMs={2000}
        />
      )}

      {!isChecking && !existingFlowId && (
        <StartAndMonitorFlow
          disabled={!canStart}
          buttonText="Generate Slide Decks"
          processingText="Generating slides..."
          startFlowFunction={startFlowFunction}
          onFlowComplete={handleFlowComplete}
          pollingIntervalMs={2000}
          resetTrigger={processFlowResetTrigger}
        />
      )}

      {error && (
        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}

      {processResult && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="success.main">
            {processResult.converted_lessons.length} slide deck
            {processResult.converted_lessons.length !== 1 ? 's' : ''} generated
          </Typography>
          {processResult.errors.length > 0 && (
            <Typography variant="body2" color="error">
              {processResult.errors.length} lesson
              {processResult.errors.length !== 1 ? 's' : ''} failed
            </Typography>
          )}
        </Box>
      )}

      {(processResult || error || existingFlowId) && (
        <Box
          sx={{
            mt: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <ResetStage
            reset={async () => {
              clearExistingFlowId()
              clearError()
              await resetProcessStage()
              setProcessFlowResetTrigger(0)
              setTimeout(() => setProcessFlowResetTrigger(1), 0)
            }}
            isResetting={isResetting}
            buttonText="Reset stage"
            dialogTitle="Reset process stage?"
            dialogContentText="This will clear the process results and any process flow."
            buttonSx={{ mt: 0 }}
          />
          {processResult && processResult.errors.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                setSelectedLessonIds(
                  new Set(processResult.errors.map((e) => e.lesson_id)),
                )
              }
            >
              Select failed lessons
            </Button>
          )}
        </Box>
      )}
    </Box>
  )
}
